import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { CORE_CONSTANT } from '@salescore/core'
import { Array, Order, pipe } from 'effect'

export interface DimensionFilterPickerOption {
  label: string
  value: string | null // nullのときは所属なし(id指定なし)
  includedInCurrentRecordValues?: boolean
}

export type DimensionFilterPickerOptions = Record<string, Record<string, DimensionFilterPickerOption[]>>

export interface DimensionFiltersState {
  /*
       options: 特殊なユースケースを実現するために仕方がなく定義している状態
      「フィルタを適用した後にやっぱり他の選択肢でフィルタしなおしたくなったときに、この状態を保持しておかないと「他の選択肢」が見えなくなってしまう
       これをローカルステートで保持しようとしたが、再フェッチ時の再レンダリングで状態がリセットされてしまった
      */
  options: DimensionFilterPickerOptions
}

const initialState: DimensionFiltersState = {
  options: {},
}

export const dimensionFiltersSlice = createSlice({
  name: 'dimensionFilters',
  initialState,
  reducers: {
    updateDimensionFilterOptions: (
      state,
      action: PayloadAction<{
        viewId: string
        dimensionKey: string
        optionsInCurrentRecords: DimensionFilterPickerOption[]
      }>,
    ) => {
      const { viewId, dimensionKey, optionsInCurrentRecords } = action.payload
      const optionValuesInCurrentRecords = new Set(
        optionsInCurrentRecords.map((x) => x.value ?? CORE_CONSTANT.KPI_PIVOT_NULL_STRING),
      )
      // eslint-disable-next-line no-param-reassign
      state.options[viewId] ??= {}
      // eslint-disable-next-line no-param-reassign
      state.options[viewId][dimensionKey] ??= []
      // eslint-disable-next-line no-param-reassign
      state.options[viewId][dimensionKey] = pipe(
        [...optionsInCurrentRecords, ...state.options[viewId][dimensionKey]],
        Array.dedupeWith((a, b) => a.value === b.value),
        Array.map((x) => ({
          ...x,
          includedInCurrentRecordValues: optionValuesInCurrentRecords.has(
            x.value ?? CORE_CONSTANT.KPI_PIVOT_NULL_STRING,
          ),
        })),
        Array.sortWith((x) => (x.includedInCurrentRecordValues ? 0 : 1), Order.Number),
      )
    },
  },
  selectors: {
    selectDimensionFilterOptions: (state, viewId: string, dimensionKey: string) =>
      state.options[viewId]?.[dimensionKey] ?? [],
  },
})

export const { updateDimensionFilterOptions } = dimensionFiltersSlice.actions
export const { selectDimensionFilterOptions } = dimensionFiltersSlice.selectors
export default dimensionFiltersSlice.reducer
